// export {}
import React, { useState, useEffect, useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Box,
  Button,
  Container,
  InputLabel,
  TextField,
  Typography,
  FormControl,
  SelectChangeEvent,
  Card,
  CardContent,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
} from '@mui/material'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import { useMutateUpdateTimeSheet } from '../../projects/timesheets/hooks'
import { useGetTimeSheetById } from './hooks'
import { useGetProject } from '../hooks'
import { Fetching } from '../../../components/Fetching'
import {
  convertTZToUTC,
  convertUTCToTZ,
  convertUTCToTZDateOnly,
  getDateTimeNowUTC,
  guessUserTZ,
  setTZForDate,
  getCurrentDateTime,
  dateIsAfter,
} from '../../../lib/time'
import { useTranslation } from 'react-i18next'
import Language from '../../../types/Language'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import MuiAlert, { AlertProps } from '@mui/material/Alert'
import { useGetEntityFormByType } from '../../../hooks/entityFormHook'
import { useGetAllCostCodes } from '../../projects/cost-codes/hooks'
import { Answer, CostCode, Field } from '../../../types/Timesheet'
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ArrowBack,
} from '@mui/icons-material'
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { SnackbarState } from '../../../types/SnackBarState'
import SnackBarContext from '../../../context/SnackBarContext'

const languages: Language[] = [
  {
    code: 'en',
    native: 'English',
  },
  {
    code: 'es',
    native: 'Spanish',
  },
]

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />
})

const MAX_TIMESHEET_COSTCODES = 5

export const Timesheet = (props: any) => {
  const { id: projectId, timesheetId } = useParams()
  const { isLoading, user } = useAuth0()
  const navigate = useNavigate()
  const showClockOutQuestions = timesheetId ? true : false
  const [authUser, setAuthUser] = useState<any | undefined>({ sub: '' })
  const { mutate } = useMutateUpdateTimeSheet('projectTimesheets', timesheetId)
  const [workDate, setWorkDate] = useState<string | undefined>(
    dayjs().format('MM/DD/YYYY')
  )
  const [workStartTime, setWorkStartTime] = useState<string | null>(null)
  const [workEndTime, setWorkEndTime] = useState<string | null>(null)
  const [workEndTimeError, setWorkEndTimeError] = useState<string | null>(null)
  const [notes, setNotes] = useState<string | undefined>()
  const { data: projectData } = useGetProject('project', projectId)
  const { toggleSnackbar } = useContext(SnackBarContext);

  const { data: tsFieldsData, isFetching: tsFieldsFetching } =
    useGetEntityFormByType('timesheet', 'timesheet')

  const { data: timesheetData, isFetching: tsDataFetching } =
    useGetTimeSheetById('projectTimesheets', timesheetId)
  const { data: costCodeData, isFetching: costCodeFetching } =
    useGetAllCostCodes('cost-codes', projectId)
  const { t, i18n } = useTranslation()
  // Retrieve user's timezone setting
  const [settings] = useLocalStorage('settings', '')
  const userTZ = settings.timeZone ? settings.timeZone : guessUserTZ()
  const [clockOutAnswers, setClockOutAnswers] = useState<Answer[]>([])
  const [areClockOutFieldsValid, setAreClockOutFieldsValid] =
    useState<boolean>(false)
  const [costCodes, setCostCodes] = useState<CostCode[]>([
    { costCode: '', percentage: 100 },
  ])

  const [isCostCodePercentageValid, setIsCostCodePercentageValid] =
    useState<boolean>(true)
  const [userRole, setUserRole] = useState<
    'vtadmin' | 'superintendent' | 'worker' | null
  >(null)
  const [, setAreAllFieldsValid] = useState<boolean>(false)
  const [isTimesheetFinalized, setIsTimesheetFinalized] =
    useState<boolean>(false)
  const [appKioskMode] = useLocalStorage('kioskMode', '')
  const [canUserEdit, setCanUserEdit] = useState<boolean>(false)
  const location = useLocation()

  // TODOS:
  // Spanish/English based on user setting
  // Superintendent can manually edit date or time fields from timesheet grid

  //   const handleWorkDate = (event: React.ChangeEvent<HTMLInputElement>) => {
  //     setWorkDate(event.target.value)
  //   }

  const handleClockIn = () => {
    // Save the timesheet and clock-in date time
    const startTime = getCurrentTime()
    setWorkStartTime(startTime)
    const workStartDateTimeTZ = setTZForDate(startTime, userTZ)
    const workStartDateTimeUTC = convertTZToUTC(workStartDateTimeTZ)
    const worker = location.state ? location.state.worker.payload.whoami : null


    const formData = { projectId, workStartDateTimeUTC, worker: worker }
    mutate(formData)
    toggleSnackbar('Your clock-in time has been saved!', 'success');
    navigateToProject()
  }

  // Only called if a worker is using this form
  const handleClockOut = () => {
    const endTime = getCurrentTime()
    if (
      timesheetId &&
      workStartTime &&
      validateClockOut(costCodes, clockOutAnswers, workStartTime, endTime)
    ) {
      setWorkEndTime(endTime)
      const workStartDateTimeTZ = setTZForDate(workStartTime, userTZ)
      const workStartDateTimeUTC = convertTZToUTC(workStartDateTimeTZ)
      const workEndDateTimeTZ = setTZForDate(endTime, userTZ)
      const workEndDateTimeUTC = convertTZToUTC(workEndDateTimeTZ)
      const worker = location.state
        ? location.state.worker.payload.whoami
        : null

      const formData = {
        _id: timesheetId,
        projectId,
        workStartDateTimeUTC,
        workEndDateTimeUTC,
        clockOutAnswers,
        costCodes,
        worker: worker,
      }
      mutate(formData)
      toggleSnackbar('Your clock-out time has been saved!', 'success');
      navigateToProject()
    } else {
      toggleSnackbar('There was an error saving your clock-out time!', 'error');
    }
  }

  const handleSubmit = (
    event:
      | React.SyntheticEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    const buttonClicked = event.currentTarget.value // we are looking for 'finalize'
    if (
      workStartTime &&
      workEndTime &&
      validateClockOut(costCodes, clockOutAnswers, workStartTime, workEndTime)
    ) {
      // May still need this for superintendent edits
      // Convert dates to UTC before sending to backend
      const workStartDateTimeTZ = setTZForDate(workStartTime, userTZ)
      const workStartDateTimeUTC = convertTZToUTC(workStartDateTimeTZ)
      const workEndDateTimeTZ = workEndTime
        ? setTZForDate(workEndTime, userTZ)
        : null
      const workEndDateTimeUTC = workEndDateTimeTZ
        ? convertTZToUTC(workEndDateTimeTZ)
        : null

      const finalizedDateUTC =
        buttonClicked === 'finalize' ? getDateTimeNowUTC() : null
      const worker = location.state ? location.state.worker.payload.email : null

      let formData: any = {
        projectId,
        workStartDateTimeUTC,
        workEndDateTimeUTC,
        notes,
        clockOutAnswers,
        costCodes,
        finalizedDateUTC,
        worker: worker,
      }
      if (timesheetId) {
        formData._id = timesheetId
      }
      mutate(formData)
      toggleSnackbar('This timesheet has been saved!', 'success');
      navigateToProject()
    } else if (!dayjs(workStartTime).isBefore(workEndTime)) {
      toggleSnackbar('End date should be greater then Start date.', 'error');
    } else {
      toggleSnackbar('There was an error saving this timesheet!', 'error');
    }
  }

  const getCurrentTime = () => {
    return getCurrentDateTime()
  }

  const handleCostCodeChange = (event: SelectChangeEvent, index: number) => {
    const currentCostCodes = costCodes.map((code, i) => {
      return i === index ? { ...code, costCode: event.target.value } : code
    })
    validateClockOut(
      currentCostCodes,
      clockOutAnswers,
      workStartTime,
      workEndTime
    )
    setCostCodes(currentCostCodes)
  }

  const handleCostCodePercentageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const currentPercentage = event.target.value
      ? parseFloat(event.target.value)
      : 0
    const currentCostCodes = costCodes.map((code, i) => {
      return i === index ? { ...code, percentage: currentPercentage } : code
    })
    const totalPercentage = currentCostCodes.reduce((total, costCode) => {
      return total + costCode.percentage
    }, 0)
    const indexToUpdate =
      index + 1 <= currentCostCodes.length - 1 ? index + 1 : 0

    if (totalPercentage > 100) {
      const percentDiff = totalPercentage - 100
      if (currentCostCodes[indexToUpdate].percentage > percentDiff) {
        currentCostCodes[indexToUpdate].percentage = parseFloat(
          (currentCostCodes[indexToUpdate].percentage - percentDiff).toFixed(2)
        )
      } else {
        let percentToRemove: number = parseFloat(percentDiff.toFixed(2))
        currentCostCodes.forEach((costCode, indx) => {
          if (indx !== index) {
            const removeAmount =
              costCode.percentage - percentToRemove < 0
                ? costCode.percentage
                : percentToRemove
            const newCostCodePercentage = costCode.percentage - removeAmount
            currentCostCodes[indx].percentage = parseFloat(
              newCostCodePercentage.toFixed(2)
            )
            percentToRemove = percentToRemove - removeAmount
          }
        })
      }
    } else if (totalPercentage < 100) {
      currentCostCodes[indexToUpdate].percentage = parseFloat(
        (
          100 -
          (totalPercentage - currentCostCodes[indexToUpdate].percentage)
        ).toFixed(2)
      )
    }
    validateClockOut(
      currentCostCodes,
      clockOutAnswers,
      workStartTime,
      workEndTime
    )
    setCostCodes(currentCostCodes)
  }

  const handleClockOutAnswers = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value: boolean = event.target.value === 'true' ? true : false
    let currentClockOutAnswers = []
    if (!clockOutAnswers[index]) {
      currentClockOutAnswers = [
        ...clockOutAnswers,
        {
          field: event.target.name,
          question: tsFieldsData.fields[index].label,
          answer: value,
        },
      ]
    } else {
      currentClockOutAnswers = clockOutAnswers.map((answer, i) => {
        return i === index ? { ...answer, answer: value } : answer
      })
    }
    validateClockOut(
      costCodes,
      currentClockOutAnswers,
      workStartTime,
      workEndTime
    )
    setClockOutAnswers(currentClockOutAnswers)
  }

  const handleEndTimeChange = (newWorkEndTime: string | null) => {
    validateClockOut(costCodes, clockOutAnswers, workStartTime, newWorkEndTime)
    setWorkEndTime(newWorkEndTime)
  }

  const handleEndTimeInput = (e: any) => {
    setWorkEndTime(e.target.value)
  }

  const handleCloseTimepicker = () => {
    let time = workEndTime
      ? dayjs(workEndTime).format('MM/DD/YYYY h:mm A')
      : null
    if (time === 'Invalid Date') {
      setWorkEndTimeError('The time is invalid.')
    } else {
      setWorkEndTimeError('')
    }
  }

  const handleNotes = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value)
  }

  const validateClockOut = (
    currentCostCodes: CostCode[],
    currentClockOutAnswers: Answer[],
    currentWorkStartTime: string | null,
    currentWorkEndTime: string | null
  ): boolean => {
    let areValid = true
    // Percentage of all cost codes should total 100
    const costCodePercentageTotal = currentCostCodes
      .map(code => code.percentage)
      .reduce((acc, value) => {
        return acc + value
      })
    // Make sure all cost code fields have selections and valid percentages
    const validCostCodes = currentCostCodes.filter(code => {
      return (
        code.percentage &&
        code.percentage > 0 &&
        code.percentage <= 100 &&
        code.costCode
      )
    })
    // Do we have valid cost codes?
    setIsCostCodePercentageValid(costCodePercentageTotal !== 100 ? false : true)
    if (
      costCodePercentageTotal !== 100 ||
      validCostCodes.length !== currentCostCodes.length
    ) {
      // TODO: Mark the cost code percent fields with validation error
      console.error('invalid cost codes')
      areValid = false
    }
    const validClockOutAnswers = currentClockOutAnswers.filter(field => {
      // TODO: Check fields.isRequired
      return field.answer !== null
    })
    if (validClockOutAnswers.length !== tsFieldsData.fields.length) {
      // TODO: Mark clock out answers as invalid
      console.error('invalid clock out answers')
      areValid = false
    }
    // Used for Clock-out check
    setAreClockOutFieldsValid(areValid)

    // Format both start and end work times before validating them
    const workStartDateTimeTZ = currentWorkStartTime
      ? setTZForDate(currentWorkStartTime, userTZ)
      : null
    const workEndDateTimeTZ = currentWorkEndTime
      ? setTZForDate(currentWorkEndTime, userTZ)
      : null
    const formattedWorkStartTimeTZ = workStartDateTimeTZ
      ? dayjs(workStartDateTimeTZ).format('MM/DD/YYYY h:mm A')
      : null
    const formattedWorkEndTimeTZ = workEndDateTimeTZ
      ? dayjs(workEndDateTimeTZ).format('MM/DD/YYYY h:mm A')
      : null
    // Check if all fields are valid for form submit
    const allFieldsValid =
      areValid &&
      formattedWorkStartTimeTZ &&
      formattedWorkEndTimeTZ &&
      dateIsAfter(formattedWorkStartTimeTZ, formattedWorkEndTimeTZ)
        ? true
        : false
    //console.log('dateIsAfter(currentWorkStartTime, currentWorkEndTime): ', dateIsAfter(currentWorkStartTime, currentWorkEndTime))
    setAreAllFieldsValid(allFieldsValid)
    return allFieldsValid
  }

  const addCostCodeFields = () => {
    // Add a new set of cost code fields
    let currentCostCodes = [
      ...costCodes,
      { costCode: '', percentage: 0 } as CostCode,
    ]
    if (currentCostCodes.length === 2) {
      // We are adding a second set of cost code fields so update percentage to 50% on both
      currentCostCodes = currentCostCodes.map((code, i) => {
        return { ...code, percentage: 50 }
      })
    }
    // Add a new set of cost code fields
    validateClockOut(
      currentCostCodes,
      clockOutAnswers,
      workStartTime,
      workEndTime
    )
    setCostCodes(currentCostCodes)
  }

  const removeCostCodeFields = (index: number) => {
    // Remove the last set of cost code fields
    let currentCostCodes = [...costCodes]
    currentCostCodes.splice(index, 1)
    if (currentCostCodes.length === 1) {
      // Only 1 cost code field is left, so set the percentage to 100%
      currentCostCodes[0].percentage = 100
    }
    validateClockOut(
      currentCostCodes,
      clockOutAnswers,
      workStartTime,
      workEndTime
    )
    setCostCodes(currentCostCodes)
  }

  const navigateToProject = () => {
    setTimeout(() => {
      window.location.href = `/projects/${projectId}`
    }, 1500)
  }

  useEffect(() => {
    if (user && !appKioskMode) {
      const currentUserRole = user[`${process.env.REACT_APP_WEB_URL}/roles`][0]
      setAuthUser(user)
      setUserRole(currentUserRole)
      if (currentUserRole === 'vtadmin') {
        setCanUserEdit(true)
      } else {
        // This is a superintendent
        setCanUserEdit(false)
      }
    } else {
      setUserRole('worker')
      setCanUserEdit(true)
    }
  }, [user, appKioskMode])

  useEffect(() => {
    if (timesheetData && tsFieldsData && costCodeData) {
      setWorkDate(
        convertUTCToTZDateOnly(timesheetData.workStartDateTimeUTC, userTZ)
      )
      const currentWorkStartTime = timesheetData.workStartDateTimeUTC
        ? convertUTCToTZ(timesheetData.workStartDateTimeUTC, userTZ)
        : null
      setWorkStartTime(currentWorkStartTime)
      const currentWorkEndTime = timesheetData.workEndDateTimeUTC
        ? convertUTCToTZ(timesheetData.workEndDateTimeUTC, userTZ)
        : null
      setWorkEndTime(currentWorkEndTime)
      setNotes(timesheetData.notes)
      // Default clock out answers
      const defaultClockOutAnswers = tsFieldsData.fields.map((field: Field) => {
        return {
          field: field.name,
          question: field.label,
          answer: field.answer,
        }
      })
      const currentClockOutAnswers =
        timesheetData.clockOutAnswers && timesheetData.clockOutAnswers.length
          ? timesheetData.clockOutAnswers
          : defaultClockOutAnswers
      setClockOutAnswers(currentClockOutAnswers)
      const currentCostCodes =
        timesheetData.costCodes && timesheetData.costCodes.length > 0
          ? timesheetData.costCodes
          : [{ costCode: '', percentage: 100 }]
      setCostCodes(currentCostCodes)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      validateClockOut(
        currentCostCodes,
        currentClockOutAnswers,
        currentWorkStartTime,
        currentWorkEndTime
      )
      setIsTimesheetFinalized(timesheetData.finalizedDateUTC ? true : false)
    } else if (
      tsFieldsData &&
      !isLoading &&
      !tsDataFetching &&
      !costCodeFetching
    ) {
      // Set clockout answers with default value if new timesheet
      const currentClockOutAnswers = tsFieldsData.fields.map((field: Field) => {
        return {
          field: field.name,
          question: field.label,
          answer: field.answer,
        }
      })
      setClockOutAnswers(currentClockOutAnswers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timesheetData,
    tsFieldsData,
    costCodeData,
    costCodeFetching,
    isLoading,
    tsDataFetching,
    userTZ,
  ])

  if (
    isLoading ||
    tsDataFetching ||
    tsFieldsFetching ||
    costCodeFetching ||
    !authUser
  ) {
    return <Fetching />
  }

  return (
    <>
      {projectData ? (
        <>
          <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
            <Container maxWidth='lg'>
              <Button
                variant='contained'
                onClick={() => navigate(`/projects/${projectId}`)}
                sx={{ marginBottom: 5 }}>
                <ArrowBack /> Back
              </Button>
              <Typography variant='h4' gutterBottom>
                {t('timesheet.title')}
              </Typography>
              {isTimesheetFinalized ? (
                <Typography variant='h6' color='secondary' gutterBottom>
                  (Finalized -{' '}
                  {convertUTCToTZ(timesheetData.finalizedDateUTC, userTZ)})
                </Typography>
              ) : null}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {languages.map(lng => (
                  <Button
                    key={lng.code}
                    color='secondary'
                    style={{
                      fontWeight:
                        i18n.resolvedLanguage === lng.code ? 'bold' : 'normal',
                    }}
                    type='submit'
                    onClick={() => i18n.changeLanguage(lng.code)}>
                    {lng.native}
                  </Button>
                ))}
              </div>
              <form onSubmit={handleSubmit}>
                <Card variant='outlined' sx={{ marginBottom: 3 }}>
                  <CardContent>
                    <>
                      <div className='formfield'>
                        <InputLabel
                          sx={{ display: 'block', fontWeight: 'bold' }}>
                          {t('timesheet.project')}
                        </InputLabel>
                        <FormControl sx={{ mt: 1, mb: 1, width: 400 }}>
                          <Typography variant='h6' gutterBottom>
                            {projectData.projectName}
                          </Typography>
                        </FormControl>
                      </div>
                      <div className='formfield'>
                        <InputLabel
                          sx={{ display: 'block', fontWeight: 'bold' }}>
                          {t('timesheet.client')}
                        </InputLabel>
                        <FormControl sx={{ mt: 1, mb: 1, width: 400 }}>
                          <Typography variant='h6' gutterBottom>
                            {projectData.clientName}
                          </Typography>
                        </FormControl>
                      </div>
                      <div className='formfield'>
                        <InputLabel
                          sx={{
                            display: 'block',
                            fontWeight: 'bold',
                            textTransform: 'capitalize',
                          }}>
                          {timesheetData?.user?.type}
                        </InputLabel>
                        <FormControl sx={{ mt: 1, mb: 1, width: 400 }}>
                          <Typography variant='h6' gutterBottom>
                            {timesheetData?.user?.firstName} &nbsp;
                            {timesheetData?.user?.lastName} &nbsp; ID: &nbsp;
                            {timesheetData?.user?.idNumber}
                          </Typography>
                        </FormControl>
                      </div>
                      <div className='formfield'>
                        <InputLabel
                          sx={{ display: 'block', fontWeight: 'bold' }}>
                          {t('timesheet.workDate')} (MM/DD/YYYY)
                        </InputLabel>
                        <FormControl sx={{ mt: 1, mb: 1, width: 300 }}>
                          <Typography variant='h6' gutterBottom>
                            {workDate}
                          </Typography>
                        </FormControl>
                      </div>
                      <div className='field-container'>
                        {userRole === 'worker' ? (
                          <>
                            <div className='field-section'>
                              <InputLabel
                                sx={{ display: 'block', fontWeight: 'bold' }}>
                                * {t('timesheet.workStartTime')}
                              </InputLabel>
                              <FormControl
                                sx={{ mt: 1, mb: 1, width: 300, mr: 2 }}>
                                <TextField
                                  id='workStartTime'
                                  name='workStartTime'
                                  placeholder=''
                                  variant='filled'
                                  fullWidth
                                  margin='normal'
                                  value={workStartTime || ''}
                                  disabled={
                                    userRole === 'worker' ||
                                    isTimesheetFinalized
                                  }
                                />
                              </FormControl>
                            </div>
                            <div className='button-section'>
                              <Button
                                variant='contained'
                                onClick={handleClockIn}
                                disabled={
                                  workStartTime || isTimesheetFinalized
                                    ? true
                                    : false
                                }
                                sx={{ mt: 4 }}>
                                {t('timesheet.clockin')}
                              </Button>
                            </div>
                          </>
                        ) : null}
                        {userRole !== 'worker' ? (
                          <div className='field-section'>
                            <InputLabel
                              sx={{
                                display: 'block',
                                fontWeight: 'bold',
                                mb: 1,
                              }}>
                              * {t('timesheet.workStartTime')}
                            </InputLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <MobileDateTimePicker
                                disableFuture
                                label=''
                                value={workStartTime}
                                onChange={newStartTime => {
                                  setWorkStartTime(
                                    newStartTime ? newStartTime : null
                                  )
                                }}
                                renderInput={params => (
                                  <TextField {...params} />
                                )}
                                disabled={isTimesheetFinalized || !canUserEdit}
                              />
                            </LocalizationProvider>
                          </div>
                        ) : null}
                      </div>

                      {(showClockOutQuestions && workStartTime && costCodes) ||
                      (userRole !== 'worker' && costCodes)
                        ? costCodes.map((costCode: any, index: number) => {
                            return (
                              <div
                                key={`costCodeFormField${index}`}
                                className='field-container'>
                                <div className='field-section'>
                                  <InputLabel
                                    sx={{
                                      display: 'block',
                                      fontWeight: 'bold',
                                    }}>
                                    * {t('timesheet.costCode')}
                                  </InputLabel>
                                  <FormControl
                                    sx={{ mt: 1, mb: 1, width: 300, mr: 2 }}
                                    error={
                                      costCodes[index] &&
                                      costCodes[index].costCode
                                        ? false
                                        : true
                                    }>
                                    <Select
                                      id={`costCode${index}`}
                                      key={`costCode${index}`}
                                      name={`costCode${index}`}
                                      placeholder='Select a cost code'
                                      variant='filled'
                                      value={
                                        costCodes[index] &&
                                        costCodes[index].costCode
                                          ? costCodes[index].costCode
                                          : ''
                                      }
                                      onChange={(e: SelectChangeEvent) =>
                                        handleCostCodeChange(e, index)
                                      }
                                      error={
                                        costCodes[index] &&
                                        costCodes[index].costCode
                                          ? false
                                          : true
                                      }
                                      disabled={
                                        (userRole === 'worker' &&
                                          workEndTime) ||
                                        isTimesheetFinalized
                                          ? true
                                          : false
                                      }>
                                      <MenuItem disabled value=''>
                                        Select a Cost Code
                                      </MenuItem>
                                      {costCodeData &&
                                        costCodeData.map(
                                          (code: any, index: number) => {
                                            return (
                                              <MenuItem
                                                key={`costcodeoption_${index}`}
                                                value={code._id}>
                                                {code.label}
                                              </MenuItem>
                                            )
                                          }
                                        )}
                                    </Select>
                                    <FormHelperText>
                                      {costCodes[index] &&
                                      costCodes[index].costCode
                                        ? ''
                                        : 'Select a Cost Code'}
                                    </FormHelperText>
                                  </FormControl>
                                </div>

                                {costCodes.length > 0 ? (
                                  <div className='field-section'>
                                    <InputLabel
                                      sx={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                      }}>
                                      * {t('timesheet.costCodePercentage')}
                                    </InputLabel>
                                    <FormControl
                                      sx={{ mt: 1, mb: 1, width: 300, mr: 2 }}>
                                      <TextField
                                        id={`costCodePercentage${index}`}
                                        key={`costCodePercentage${index}`}
                                        name={`costCodePercentage${index}`}
                                        type='number'
                                        placeholder='Enter percentage worked'
                                        variant='filled'
                                        fullWidth
                                        margin='normal'
                                        value={
                                          costCodes[index] &&
                                          costCodes[index].percentage
                                            ? costCodes[index].percentage
                                            : ''
                                        }
                                        onChange={(
                                          e: React.ChangeEvent<HTMLInputElement>
                                        ) =>
                                          handleCostCodePercentageChange(
                                            e,
                                            index
                                          )
                                        }
                                        error={
                                          costCodes[index] &&
                                          (!costCodes[index].percentage ||
                                            !isCostCodePercentageValid)
                                            ? true
                                            : false
                                        }
                                        helperText={
                                          costCodes[index] &&
                                          (!costCodes[index].percentage ||
                                            !isCostCodePercentageValid)
                                            ? 'Enter percent worked. Must total 100%.'
                                            : ''
                                        }
                                        disabled={
                                          (userRole === 'worker' &&
                                            workEndTime) ||
                                          isTimesheetFinalized
                                            ? true
                                            : false
                                        }
                                        sx={{ marginTop: 0 }}
                                      />
                                    </FormControl>
                                  </div>
                                ) : null}

                                <div className='button-section'>
                                  {index && index === costCodes.length - 1 ? (
                                    <Button
                                      variant='contained'
                                      color='secondary'
                                      onClick={() =>
                                        removeCostCodeFields(index)
                                      }
                                      disabled={
                                        (userRole === 'worker' &&
                                          workEndTime) ||
                                        isTimesheetFinalized
                                          ? true
                                          : false
                                      }
                                      sx={{ mt: 2 }}>
                                      <RemoveIcon />
                                    </Button>
                                  ) : null}
                                  {index === costCodes.length - 1 &&
                                  costCodes.length < MAX_TIMESHEET_COSTCODES &&
                                  costCodes[0].costCode ? (
                                    <Button
                                      variant='contained'
                                      color='secondary'
                                      onClick={() => addCostCodeFields()}
                                      disabled={
                                        (userRole === 'worker' &&
                                          workEndTime) ||
                                        isTimesheetFinalized
                                          ? true
                                          : false
                                      }
                                      sx={{ mt: 2, ml: 2 }}>
                                      <AddIcon />
                                    </Button>
                                  ) : null}
                                </div>
                              </div>
                            )
                          })
                        : null}

                      {clockOutAnswers &&
                      tsFieldsData &&
                      tsFieldsData.fields &&
                      ((showClockOutQuestions && workStartTime) ||
                        userRole !== 'worker')
                        ? tsFieldsData.fields.map(
                            (field: any, index: number) => {
                              return (
                                <div
                                  key={`clockoutFormField${index}`}
                                  className='formfield'>
                                  <InputLabel
                                    sx={{
                                      display: 'block',
                                      fontWeight: 'bold',
                                      wordWrap: 'break-word',
                                      whiteSpace: 'normal',
                                    }}>
                                    {'* ' + t('timesheet.hr' + index)}
                                  </InputLabel>
                                  <RadioGroup
                                    key={`clockOutField${index}`}
                                    aria-labelledby={`${field.name}_label`}
                                    name={field.name}
                                    row
                                    value={
                                      clockOutAnswers[index]
                                        ? clockOutAnswers[index].answer
                                        : null
                                    }
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>
                                    ) => handleClockOutAnswers(e, index)}>
                                    {field.options &&
                                      field.options.map(
                                        (option: any, index: number) => (
                                          <FormControlLabel
                                            key={`clockoutOption${index}`}
                                            value={option.value}
                                            control={
                                              <Radio
                                                disabled={
                                                  (workEndTime &&
                                                    userRole === 'worker') ||
                                                  isTimesheetFinalized ||
                                                  !canUserEdit
                                                    ? true
                                                    : false
                                                }
                                              />
                                            }
                                            label={
                                              option.label === 'Yes'
                                                ? t('timesheet.yes')
                                                : t('timesheet.no')
                                            }
                                          />
                                        )
                                      )}
                                  </RadioGroup>
                                </div>
                              )
                            }
                          )
                        : null}

                      {(showClockOutQuestions && workStartTime) ||
                      userRole !== 'worker' ? (
                        <div className='field-container'>
                          {userRole === 'worker' ? (
                            <>
                              <div className='field-section'>
                                <InputLabel
                                  sx={{ display: 'block', fontWeight: 'bold' }}>
                                  * {t('timesheet.workEndTime')}
                                </InputLabel>
                                <FormControl sx={{ mb: 1, width: 300, mr: 2 }}>
                                  <TextField
                                    id='workEndTime'
                                    name='workEndTime'
                                    placeholder=''
                                    variant='filled'
                                    fullWidth
                                    margin='normal'
                                    value={workEndTime}
                                    disabled={
                                      userRole === 'worker' ||
                                      isTimesheetFinalized
                                    }
                                  />
                                </FormControl>
                              </div>
                              <div className='button-section'>
                                <Button
                                  variant='contained'
                                  onClick={handleClockOut}
                                  disabled={
                                    !workStartTime ||
                                    workEndTime ||
                                    !areClockOutFieldsValid ||
                                    isTimesheetFinalized
                                      ? true
                                      : false
                                  }
                                  sx={{ mt: 4 }}>
                                  {t('timesheet.clockout')}
                                </Button>
                              </div>
                            </>
                          ) : null}
                          {userRole !== 'worker' ? (
                            <div className='field-section'>
                              <InputLabel
                                sx={{
                                  display: 'block',
                                  fontWeight: 'bold',
                                  mb: 1,
                                }}>
                                * {t('timesheet.workEndTime')}
                              </InputLabel>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MobileDateTimePicker
                                  disableFuture
                                  label=''
                                  value={workEndTime}
                                  onChange={newEndTime => {
                                    handleEndTimeChange(newEndTime)
                                  }}
                                  onClose={() => handleCloseTimepicker()}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      onInput={e => handleEndTimeInput(e)}
                                    />
                                  )}
                                  disabled={
                                    isTimesheetFinalized || !canUserEdit
                                  }
                                />
                              </LocalizationProvider>
                              <p style={{ color: 'red' }}>
                                {workEndTimeError ? workEndTimeError : ''}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {userRole !== 'worker' ? (
                        <div className='formfield'>
                          <InputLabel
                            sx={{ display: 'block', fontWeight: 'bold' }}>
                            {t('timesheet.notes')}
                          </InputLabel>
                          <TextField
                            id='notes'
                            name='notes'
                            placeholder={t(
                              'timesheet.notesPlaceholder'
                            ).toString()}
                            multiline
                            rows={4}
                            variant='filled'
                            fullWidth
                            margin='normal'
                            value={notes}
                            onChange={handleNotes}
                            disabled={isTimesheetFinalized}
                          />
                        </div>
                      ) : null}
                    </>
                  </CardContent>
                </Card>
                {userRole !== 'worker' ? (
                  <Stack spacing={2} direction='row'>
                    <Button
                      value='save'
                      variant='contained'
                      onClick={e => handleSubmit(e)}
                      disabled={isTimesheetFinalized}>
                      {t('timesheet.submit')}
                    </Button>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={() => navigate(-1)}>
                      {t('timesheet.cancel')}
                    </Button>
                  </Stack>
                ) : null}
                <br />
              </form>
            </Container>
          </Box>
        </>
      ) : null}
    </>
  )
}
